import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { normalizePlatformAccess } from '@/lib/utils';
import { COMMODITYBID_EXTERNAL_URL } from '@/config/portalUrls';
import BrandLogo from '@/components/BrandLogo';
import {
  LayoutDashboard, FileText, TrendingUp, History,
  Anchor, MessageSquare, User,
  ChevronDown, LogOut, Bell, Package, Upload, ClipboardCheck, BellRing, Navigation,
  Plus, ExternalLink, Search,
} from 'lucide-react';
import { useState } from 'react';
import { NotificationProvider, useNotifications } from '@/contexts/NotificationContext';
import { primeNotificationAudio } from '@/lib/notificationSound';

const SideSection = ({ label, color, children }) => (
  <div className="mb-2">
    <p className={`text-xs font-semibold uppercase tracking-widest px-3 py-1.5 ${color}`}>{label}</p>
    {children}
  </div>
);

const ExternalNavItem = ({ href, icon: Icon, label, testId }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-semibold transition-all duration-150 text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-100"
  >
    <Icon className="w-4 h-4 flex-shrink-0" />
    {label}
    <ExternalLink className="w-3.5 h-3.5 ml-auto opacity-70" aria-hidden />
  </a>
);

const NavItem = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-150 ${
        isActive
          ? 'bg-slate-100 text-slate-900 font-semibold'
          : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
      }`
    }
  >
    <Icon className="w-4 h-4 flex-shrink-0" />
    {label}
  </NavLink>
);

function CustomerLayoutInner() {
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const platform = normalizePlatformAccess(user);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const initials = (user?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 h-screen fixed top-0 left-0 flex flex-col z-20 overflow-y-auto">
        {/* Logo */}
        <div className="h-16 flex items-center px-5 border-b border-slate-200 flex-shrink-0">
          <BrandLogo variant="mark" className="max-w-[210px]" />
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" />

          {platform.demaxtore && (
            <SideSection label="DeMaxtore" color="text-sky-600">
              <NavItem to="/demaxtore/rfqs" icon={FileText} label="My RFQs" testId="nav-demaxtore-rfqs" />
              <NavItem to="/demaxtore/purchase-orders" icon={Upload} label="Purchase order" testId="nav-demaxtore-po" />
              <NavItem to="/demaxtore/inspection-report" icon={ClipboardCheck} label="Inspection report" testId="nav-demaxtore-inspection" />
              <NavItem to="/demaxtore/notifications" icon={BellRing} label="Notifications" testId="nav-demaxtore-notifications" />
              <NavItem to="/demaxtore/live-shipment-tracking" icon={Navigation} label="Live shipment tracking" testId="nav-demaxtore-live-tracking" />
            </SideSection>
          )}

          {platform.commoditybid && (
            <SideSection label="CommodityBid" color="text-orange-600">
              <ExternalNavItem
                href={COMMODITYBID_EXTERNAL_URL}
                icon={Plus}
                label="Create Bid"
                testId="nav-commodity-create-bid"
              />
              <NavItem to="/commoditybid/live-bids" icon={TrendingUp} label="Pending Bids" testId="nav-pending-bids" />
              <NavItem to="/commoditybid/history" icon={History} label="Completed Bids" testId="nav-completed-bids" />
            </SideSection>
          )}

          {platform.freightiq && (
            <SideSection label="FreightIQ" color="text-green-600">
              <NavItem to="/freightiq/requests" icon={Search} label="Freight search" testId="nav-freight-search" />
              <NavItem to="/freightiq/offers" icon={Anchor} label="Freight offers" testId="nav-freight-offers" />
              <NavItem to="/freightiq/history" icon={History} label="Freight history" testId="nav-freight-history" />
            </SideSection>
          )}

          <div className="pt-2 border-t border-slate-100 mt-2 space-y-0.5">
            <NavItem to="/messages" icon={MessageSquare} label="Messages" testId="nav-messages" />
            <NavItem to="/profile" icon={User} label="Profile" testId="nav-profile" />
          </div>
        </nav>

        {/* User footer */}
        <div className="p-3 border-t border-slate-200 flex-shrink-0">
          <button
            onClick={() => setUserMenuOpen(v => !v)}
            data-testid="user-menu-button"
            className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 transition-colors group"
          >
            <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">{initials}</span>
            </div>
            <div className="flex-1 text-left overflow-hidden">
              <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.company_name}</p>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
          </button>
          {userMenuOpen && (
            <div className="mt-1 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="ml-64 flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-10">
          <div />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                primeNotificationAudio();
                navigate('/demaxtore/notifications');
              }}
              className="relative p-2 rounded-lg hover:bg-slate-50 transition-colors"
              title="Bildirimler"
              data-testid="notifications-button"
            >
              <Bell className="w-5 h-5 text-slate-500" />
              {unreadCount > 0 && (
                <span
                  className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-sky-600 px-1 text-[10px] font-bold text-white"
                  data-testid="notifications-unread-badge"
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center">
                <span className="text-white text-xs font-bold">{initials}</span>
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-semibold text-slate-900 leading-tight">{user?.name}</p>
                <p className="text-xs text-slate-400">Customer</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default function CustomerLayout() {
  return (
    <NotificationProvider>
      <CustomerLayoutInner />
    </NotificationProvider>
  );
}
